import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetWatersBySpecies,
  GetWatersBySpeciesVariables,
  GetTopSpeciesNearLocation,
  GetTopSpeciesNearLocationVariables,
  GetSpeciesImages,
  GetSpeciesImagesVariables,
  GetSpeciesTopWaters,
  GetSpeciesTopWatersVariables,
} from '@generated/graphql';
import { CatchFragments } from '@graphql/fragments/rutilus/CatchType';
import { FishingWaterFragments } from '@graphql/fragments/rutilus/FishingWaterType';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { PostFragments } from '@graphql/fragments/rutilus/PostType';
import { ProductUnitFragments } from '@graphql/fragments/rutilus/ProductUnitType';
import { SpeciesFragments } from '@graphql/fragments/rutilus/SpeciesTypes';

export const getDirectorySpecies = gql`
  query GetDirectorySpecies($first: Int!, $after: String, $startsWith: String) {
    entry: species(
      first: $first
      after: $after
      filters: { startsWith: $startsWith, orderedByName: true }
    ) {
      edges {
        cursor
        node {
          externalId
          displayName
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`;

export const getSingleSpeciesMetadata = gql`
  query GetSingleSpeciesMetadata($id: String!) {
    entry: singleSpecies(externalId: $id) {
      ...SpeciesId
      displayName
      exampleCatchWithImage {
        caughtAtGmt
        species {
          ...SpeciesId
          displayName
        }
        post {
          ...PostId
          images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
            totalCount
            edges {
              node {
                ...ImageFields
              }
            }
          }
        }
      }
    }
  }
  ${PostFragments.id}
  ${SpeciesFragments.id}
  ${ImageFragments.imageFields}
`;

export const getSingleSpecies = gql`
  query GetSingleSpecies($id: String!, $recentCatchLimitDays: Int) {
    entry: singleSpecies(externalId: $id) {
      ...SingleSpeciesFields
    }
  }
  ${SpeciesFragments.singleSpeciesFields}
  ${SpeciesFragments.speciesTipNodeFields}
  ${CatchFragments.speciesExampleCatchFields}
  ${CatchFragments.speciesCatchWithImageNodeFields}
  ${PostFragments.productUnits}
  ${PostFragments.videoFields}
  ${PostFragments.id}
`;

export const getSpeciesTopBaits = gql`
  query GetSpeciesTopBaits($speciesId: String, $first: Int!) {
    topProductUnits(first: $first, speciesExternalId: $speciesId) {
      pageInfo {
        hasNextPage
      }
      edges {
        node {
          ...SpeciesTopBaitNodeFields
        }
      }
    }
  }
  ${ProductUnitFragments.speciesTopBaitNodeFields}
`;

export const getSpeciesTopWaters: TypedDocumentNode<
  GetSpeciesTopWaters,
  GetSpeciesTopWatersVariables
> = gql`
  query GetSpeciesTopWaters(
    $first: Int
    $boundingBox: BoundingBoxInputObject
    $speciesIDs: [String!]
  ) {
    entry: fishingWaters(
      first: $first
      filters: {
        orderByCatchesCount: true
        publicOnly: true
        withCatchesForSpeciesIds: $speciesIDs
      }
      boundingBox: $boundingBox
    ) {
      edges {
        node {
          ...FishingWaterId
          ...FishingWaterLocationFields
          catchesCount
          displayName
          allReviews: reviews {
            totalCount
            totalAverage(attribute: RATING)
          }
          recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
            edges {
              node {
                ...CatchId
                post {
                  ...PostId
                  images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                    totalCount
                    edges {
                      node {
                        ...ImageFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

export const getTopSpeciesNearLocation: TypedDocumentNode<
  GetTopSpeciesNearLocation,
  GetTopSpeciesNearLocationVariables
> = gql`
  query GetTopSpeciesNearLocation(
    $boundingBox: BoundingBoxInputObject
    $boundingCircle: BoundingCircleInputObject
    $excludingSpeciesIds: [String!]
    $first: Int!
    $after: String
  ) {
    topSpeciesNearLocation(
      boundingCircle: $boundingCircle
      boundingBox: $boundingBox
      excludingSpeciesExternalIds: $excludingSpeciesIds
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        startCursor
        hasNextPage
        endCursor
      }
      edges {
        catchesCount
        node {
          ...TopSpeciesNearLocationNodeFields
        }
      }
    }
  }
  ${SpeciesFragments.topSpeciesNearLocationNodeFields}
  ${CatchFragments.id}
  ${PostFragments.id}
`;

export const getSingleSpeciesName = gql`
  query GetSingleSpeciesName($id: Int!) {
    species: singleSpecies(id: $id) {
      ...SpeciesId
      displayName
    }
  }
  ${SpeciesFragments.id}
`;

export const getSpeciesImages: TypedDocumentNode<GetSpeciesImages, GetSpeciesImagesVariables> = gql`
  query GetSpeciesImages($ids: [String!]) {
    species(externalIds: $ids, first: 300) {
      edges {
        node {
          ...SpeciesImageNodeFields
        }
      }
    }
  }
  ${SpeciesFragments.speciesImageNodeFields}
`;

// Get a specific list of species by their IDs
export const getSpeciesFromList = gql`
  query GetSpeciesFromList($externalIds: [String!], $first: Int = 10) {
    species(externalIds: $externalIds, first: $first) {
      edges {
        ...SpeciesListEdgeFields
      }
    }
  }
  ${SpeciesFragments.speciesListEdgeFields}
  ${CatchFragments.id}
  ${PostFragments.id}
`;

// Get a specific list of species by their IDs including state specific data
export const getRegionalSpeciesFromList = gql`
  query GetRegionalSpeciesFromList(
    $first: Int!
    $externalIds: [String!]
    $stateCode: String
    $countryId: String
  ) {
    species(externalIds: $externalIds, first: $first) {
      edges {
        node {
          ...RegionalSpeciesNodeFields
        }
      }
    }
  }
  ${SpeciesFragments.regionalSpeciesNodeFields}
`;

export const getBasicSpeciesInfoFromList = gql`
  query GetBasicSpeciesInfoFromList($externalIds: [String!]) {
    species(externalIds: $externalIds, first: 10) {
      edges {
        node {
          ...SpeciesBasicInfoNodeFields
        }
      }
    }
  }
  ${SpeciesFragments.speciesBasicInfoNodeFields}
`;

// Returns species display names ordered by global popularity. Used by the
// fishing-water in-season FAQ entry to surface the most relevant 5 of the
// species currently in season.
export const getSpeciesInSeasonNames = gql`
  query GetSpeciesInSeasonNames($externalIds: [String!]!, $first: Int = 5) {
    species(externalIds: $externalIds, first: $first, filters: { orderedByPopularity: true }) {
      edges {
        node {
          _id: externalId
          displayName
        }
      }
    }
  }
`;

// get number of fishing waters for a list of species within a bounding box
export const getWatersBySpecies: TypedDocumentNode<
  GetWatersBySpecies,
  GetWatersBySpeciesVariables
> = gql`
  query GetWatersBySpecies($boundingBox: BoundingBoxInputObject, $speciesIDs: [String!]) {
    entry: fishingWaters(
      filters: { publicOnly: true, withCatchesForSpeciesIds: $speciesIDs }
      boundingBox: $boundingBox
    ) {
      totalCount
    }
  }
`;
